import { FC, useEffect, useState } from "react"

import { ReactComponent as IconQuestionMark } from "@common_assets/svg/question.svg"
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import IconWrapper from "components/common/icon/iconWrapper"
import { useTranslation } from "react-i18next"

const ATTENDANCE_REFERENCE_HEIGHT = 60
const ATTENDANCE_INPUT_MIN_HEIGHT = 84

interface ClassAttendanceFormProps {
  value: string | null
  onChange: (value: string | null) => void
  onValidChange: (isValid: boolean) => void
  showErrors?: boolean
  schoolDefaultAmount?: string | null
}

const ClassAttendanceForm: FC<ClassAttendanceFormProps> = ({
  value,
  onChange,
  onValidChange,
  showErrors = false,
  schoolDefaultAmount = null,
}) => {
  const { t } = useTranslation()
  const [isCustom, setIsCustom] = useState(value !== null)
  const [amount, setAmount] = useState(value ?? "")
  const [error, setError] = useState("")

  const getSubmitError = (checked: boolean, raw: string): string => {
    if (!checked) return ""
    if (raw === "" || isNaN(Number(raw))) {
      return t("addNewClass.attendanceBonusRequired")
    }
    if (Number(raw) < 0) {
      return t("addNewClass.attendanceBonusMin")
    }
    return ""
  }

  useEffect(() => {
    if (!showErrors) return
    setIsCustom(true)
    setError(getSubmitError(true, amount))
  }, [showErrors, amount, t])

  const handleCheckboxChange = (checked: boolean) => {
    setIsCustom(checked)
    if (!checked) {
      setError("")
      onChange(null)
      onValidChange(true)
      return
    }
    if (amount === "" || isNaN(Number(amount))) {
      setError("")
      onChange(null)
      onValidChange(false)
      return
    }
    if (Number(amount) < 0) {
      setError(t("addNewClass.attendanceBonusMin"))
      onChange(null)
      onValidChange(false)
      return
    }
    setError("")
    onChange(String(Number(amount)))
    onValidChange(true)
  }

  const handleAmountChange = (raw: string) => {
    setAmount(raw)
    if (!isCustom) return
    if (raw === "") {
      setError("")
      onChange(null)
      onValidChange(false)
      return
    }
    if (isNaN(Number(raw))) {
      setError(t("addNewClass.attendanceBonusRequired"))
      onChange(null)
      onValidChange(false)
      return
    }
    const num = Number(raw)
    if (num < 0) {
      setError(t("addNewClass.attendanceBonusMin"))
      onChange(null)
      onValidChange(false)
      return
    }
    setError("")
    onChange(String(num))
    onValidChange(true)
  }

  return (
    <Box mt={4} px={1}>
      <Typography
        variant="body1"
        fontWeight="500"
        sx={{ mb: 3, lineHeight: "24px", color: "mockup.grey70" }}
      >
        {t("addNewClass.attendanceTabDescription")}
      </Typography>
      <Stack direction="row" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              checked={isCustom}
              onChange={(e) => handleCheckboxChange(e.target.checked)}
            />
          }
          label={t("addNewClass.attendanceOverride")}
        />
        <IconWrapper>
          <Tooltip
            title={t("addNewClass.attendanceOverrideTooltip")}
            slotProps={{
              tooltip: {
                sx: {
                  color: "mockup.grey70",
                },
              },
            }}
          >
            <IconQuestionMark tabIndex={0} />
          </Tooltip>
        </IconWrapper>
      </Stack>
      <Box
        sx={{
          mt: 2,
          minHeight: ATTENDANCE_INPUT_MIN_HEIGHT,
        }}
      >
        <Collapse in={!isCustom} unmountOnExit>
          <Box
            role="status"
            sx={{
              p: 2,
              height: ATTENDANCE_REFERENCE_HEIGHT,
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              borderRadius: 1,
              border: "1px dashed",
              borderColor: "mockup.neutral90",
              bgcolor: "mockup.neutral95",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: "mockup.grey70", lineHeight: "20px" }}
            >
              {schoolDefaultAmount == null
                ? t("addNewClass.attendanceBonusInherited")
                : schoolDefaultAmount !== "" &&
                    !isNaN(Number(schoolDefaultAmount))
                  ? t("addNewClass.attendanceBonusSchoolReference", {
                      amount: Number(schoolDefaultAmount),
                    })
                  : t("addNewClass.attendanceBonusInvalidAmount")}
            </Typography>
          </Box>
        </Collapse>
        <Collapse in={isCustom} unmountOnExit>
          <TextField
            type="number"
            value={amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            label={t("addNewClass.attendanceBonusAmount")}
            error={!!error}
            helperText={error || " "}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "mockup.neutral100",
              },
            }}
            slotProps={{
              htmlInput: { step: "0.01" },
              formHelperText: {
                sx: { minHeight: 20, m: 0, mt: 0.5 },
              },
            }}
          />
        </Collapse>
      </Box>
    </Box>
  )
}

export default ClassAttendanceForm
